import React, { useState, useEffect, useRef, useContext } from "react";
import {
  Table,
  ScrollArea,
  rem,
  Text,
  UnstyledButton,
  Center,
  Select,
  Pagination,
  Checkbox,
  Tabs,
} from "@mantine/core";
import {
  IconSearch,
  IconSelector,
  IconChevronDown,
  IconChevronUp,
  IconTrash,
  IconEdit,
  IconUserPlus,
  IconEye,
  IconTruck,
} from "@tabler/icons-react";
import classes from "./Order.module.css";
// import DeleteStaff from "./DeleteStaff";
import { RowDataOrder } from "./types";
import { props } from "./props";
import { CustomTable } from "../Table/CustomTable";
import { orders } from "../types/RowDataTypes";
import { useFetchRowData } from "../hooks/useFetchRowData";
import { SearchBar } from "../SearchBar/SearchBar";
import useAxiosPrivate from "../../auth/useAxiosPrivate";
import { useLocation, useNavigate } from "react-router-dom";
import {
  showErrorAlert,
  showEditWarning,
  showSuccessAlert,
} from "../../helper/alerts";
import Header from "../static/Header";
import EditOrder from "./EditOrder";
import getOrderTable from "../../DataDisplay/DataDisplayTable/OrderTable";

enum ColumnHeaderToFilterPair {
  ORDER_ID = "Order ID",
  UID = "UID",
  P_TRANSACTION_ID = "Transaction ID",
  TRACKING_NUMBER = "Tracking Number",
  COURIER_SERVICE = "Courier Service",
  MERCH_NAME = "Merchandise Name",
}

const colFilter = ColumnHeaderToFilterPair;
const colFilters: { [key in ColumnHeaderToFilterPair]: string } = {
  [colFilter.ORDER_ID]: "order_id",
  [colFilter.UID]: "uid",
  [colFilter.P_TRANSACTION_ID]: "p_transaction_id",
  [colFilter.TRACKING_NUMBER]: "tracking_number",
  [colFilter.COURIER_SERVICE]: "courier_service",
  [colFilter.MERCH_NAME]: "merch_name",
};

function Th({ children, sortable, reversed, sorted, onSort }: props) {
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;

  let allowedSorts;
  if (window.location.href.split("/")[4] === "pass")
    allowedSorts = [
      colFilter.COURIER_SERVICE.toString().toLowerCase(),
      colFilter.MERCH_NAME.toString().toLowerCase(),
    ];
  else allowedSorts = [colFilter.MERCH_NAME.toString().toLowerCase()];
  const isCurrent =
    window.location.href.split("/")[5] === "current" ? true : false;
//   console.log("the children");
//   console.log(children);
//   console.log(allowedSorts);
//   console.log(allowedSorts.includes(children!!.toString().toLowerCase()));

  return children!! === "action" && !isCurrent ? (
    <></>
  ) : (
    <Table.Th className={`${classes.th} ${sortable ? classes.sortable : ""}`}>
      {allowedSorts.includes(children!!.toString().toLowerCase()) ? (
        <UnstyledButton onClick={onSort} className={classes.control}>
          <div className={classes.sortableHeader}>
            <Text fw={500} fz="sm">
              {children}
            </Text>
            <Center className={classes.icon}>
              <Icon style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
            </Center>
          </div>
        </UnstyledButton>
      ) : (
        <Text fw={500} fz="sm">
          {children}
        </Text>
      )}
    </Table.Th>
  );
}

const initialData: RowDataOrder[] = [
  {
    uid: "",
    order_id: "",
    p_transaction_id: "",
    tracking_number: "",
    courier_service: "",
    merch_name: "",
    action: "",
  },
];

export function Order() {
  const axiosPrivate = useAxiosPrivate();

  const [search, setSearch] = useState<string>("");
  const [sortedData, setSortedData] = useState(initialData);
  const [sortBy, setSortBy] = useState<keyof RowDataOrder | null>(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [tableStateChanged, setTableStateChanged] = useState(0);
  const changeTableState = () => {
    setTableStateChanged(Math.random() * Math.random());
  };

  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const resetPage = () => {
    if (currentPage !== 1) {
      setCurrentPage(1);
      return true;
    }
    return false;
  };

  const checkboxesFilter: JSX.Element[] = [];

  //Set allowed search filter checkbox to be shown
  const allowedFilters = [
    colFilters[colFilter.UID],
    colFilters[colFilter.ORDER_ID],
    colFilters[colFilter.P_TRANSACTION_ID],
    colFilters[colFilter.MERCH_NAME],
  ];

  for (const [key, value] of Object.entries(colFilters)) {
    if (allowedFilters.includes(value)) {
      const checkboxFilter: JSX.Element = (
        <Checkbox value={value} label={key} />
      );
      checkboxesFilter.push(checkboxFilter);
    }
  }

  const [showFilterOptions, setShowFilterOptions] = useState(false);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    setSearch(input);
  };

  const location = useLocation();
  const fetchedData = useFetchRowData(
    itemsPerPage,
    currentPage,
    search,
    selectedFilters,
    tableStateChanged,
    sortBy ?? "createdAt",
    reverseSortDirection ? "DESC" : "ASC",
    orders,
    getOrderTable().create(),
    {
      current: location.pathname.split("/")[2] ?? "current",
    },
  );

  const itemsOnPage = useRef(0);
  const totalRows = fetchedData.totalRows;
  const rowData: RowDataOrder[] | null = fetchedData.data as RowDataOrder[];
  const isLoading: boolean = fetchedData.isLoading;
  const [RowDataOrder, setRowDataOrder] = useState<RowDataOrder[] | null>(null);

  useEffect(() => {
    setRowDataOrder(rowData);
    itemsOnPage.current = fetchedData.itemsOnPage;
  }, [rowData]);

  const [selectedRow, setSelectedRow] = useState<RowDataOrder | null>(null);
  const [isEditMode, setEditMode] = useState(false);
  const handleEdit = (row: RowDataOrder) => {
    setSelectedRow(row);

    // Check if the current page is "pass", then show the edit warning
    if (location.pathname.split("/")[2] === "pass") {
      showEditWarning("Are you sure you want to edit this order?", setEditMode);
    } else {
      // If the page is not "pass", directly setEditMode to true
      setEditMode(true);
    }
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const handleSaveChanges = (
    editedData: RowDataOrder,
    originalData: RowDataOrder | null
  ) => {
    if (originalData) {
      const paramsToUpdate = {
        order_id: originalData.order_id,
        tracking_number: editedData.tracking_number,
        courier_service: editedData.courier_service,
      };

      axiosPrivate
        .post("/api/admin/orders/update", paramsToUpdate)
        .then((response) => {
          if (
            itemsOnPage.current == 1 &&
            currentPage !== 1 &&
            currentPage == Math.ceil(totalRows / itemsPerPage)
          )
            setCurrentPage((prev) => prev - 1);
          else changeTableState();
          showSuccessAlert("Order Successfully Updated");
        })
        .catch((error) => {
          showErrorAlert("An error has occured");
        });
    }

    setEditMode(false);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (value: number) => {
    setCurrentPage(1);
    setItemsPerPage(value);
  };

  const isCurrent =
    location.pathname.split("/")[2] === "current" ? true : false;
  const rowsToMap = RowDataOrder ?? [];
  const rows = rowsToMap.map((row, index) => (
    <Table.Tr key={index}>
      <Table.Td>{startIndex + index + 1}</Table.Td>
      <Table.Td style={{ width: "5%" }}>{row.order_id}</Table.Td>
      <Table.Td style={{ width: "5%" }}>{row.uid}</Table.Td>
      <Table.Td style={{ width: "5%" }}>{row.p_transaction_id}</Table.Td>
      <Table.Td style={{ width: "5%" }}>{row.tracking_number}</Table.Td>
      <Table.Td style={{ width: "5%" }}>{row.courier_service}</Table.Td>
      <Table.Td style={{ width: "5%" }}>{row.merch_name}</Table.Td>
      <Table.Td className={classes.actionButtons} style={{ width: "10%" }}>
        <button onClick={() => handleEdit(row)} className={classes.editbutton}>
          <IconEdit /> Edit
        </button>
      </Table.Td>
    </Table.Tr>
  ));

  const handleSelectPage = (selectedPage: number) => {
    if (selectedPage != currentPage) setCurrentPage(selectedPage);
  };

  const nav = useNavigate();

  const handleTabChange = (val: string | null) => {
    val = val ?? "current";
    nav(`/order/${val}`);
    changeTableState();
  };

  const handleSorting = (val: any) => {
    setReverseSortDirection(!reverseSortDirection);
    setSortBy(val);
    changeTableState();
    // console.log("sorting");
    // console.log(val);
    // console.log(reverseSortDirection);
  };

  return (
    <div className={classes.orderContainer}>
      <ScrollArea className={classes.parentContainer}>
        <Header
          title={
            <div>
              <p>Order</p>
            </div>
          }
        />

        <div className={classes.searchBarContainer}>
          <SearchBar
            isLoading={isLoading}
            resetPage={resetPage}
            changeTableState={changeTableState}
            search={search}
            handleSearch={handleSearch}
            setShowFilterOptions={setShowFilterOptions}
            showFilterOptions={showFilterOptions}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            checkboxesFilter={checkboxesFilter}
          />
        </div>


        <Tabs
          defaultValue="current"
          color="blueviolet"
          variant="pills"
          onChange={(value) => {
            handleTabChange(value);
          }}
        >
          <Tabs.List className={classes.tabHeader}>
            <Tabs.Tab value="current" className={classes.tabTitle}>
              Current
            </Tabs.Tab>
            <Tabs.Tab value="pass" className={classes.tabTitle}>
              Pass
            </Tabs.Tab>
          </Tabs.List>
          <ScrollArea
            w={
              window.innerWidth -
              document.getElementById("nav-bar")!!.getBoundingClientRect().width
            }
          >

            <Tabs.Panel value="current">
              <CustomTable
                colHeaders={Object.values(ColumnHeaderToFilterPair)}
                filters={Object.values(colFilters)}
                reverseSortDirection={reverseSortDirection}
                sortBy={sortBy}
                setSorting={handleSorting}
                customclasses={classes}
                Th={Th}
                rowData={rowData}
                rows={rows}
              />
            </Tabs.Panel>

            <Tabs.Panel value="pass">
              <CustomTable
                colHeaders={Object.values(ColumnHeaderToFilterPair)}
                filters={Object.values(colFilters)}
                reverseSortDirection={reverseSortDirection}
                sortBy={sortBy}
                setSorting={handleSorting}
                customclasses={classes}
                Th={Th}
                rowData={rowData}
                rows={rows}
              />
            </Tabs.Panel>
          </ScrollArea>

        </Tabs>

        <div className={classes.footerContainer}>
          <div className={classes.resultCount}>
            <Text>
              {`Showing `}
              <span style={{ fontWeight: "bold" }}>{startIndex + 1}</span>
              {`-`}
              <span style={{ fontWeight: "bold" }}>
                {Math.min(currentPage * itemsPerPage, totalRows)}
              </span>
              {` of `}
              <span style={{ fontWeight: "bold" }}>{totalRows}</span>
              {` results `}
            </Text>
          </div>

          <div className={classes.rightContainer}>
            <div className={classes.rowsPerPageSelector}>
              <Select
                placeholder="Rows per page"
                data={["10", "20", "50"]}
                value={itemsPerPage.toString()}
                onChange={(value) =>
                  handleRowsPerPageChange(parseInt(value as string, 10))
                }
              />
            </div>

            <div className={classes.paginationSelector}>
              <Pagination
                color="blueviolet"
                value={currentPage}
                total={Math.ceil(totalRows / itemsPerPage)}
                onNextPage={() => handlePageChange(currentPage + 1)}
                onPreviousPage={() => handlePageChange(currentPage - 1)}
                onChange={(value: number) => handleSelectPage(value)}
              />
            </div>
          </div>
        </div>

        {isEditMode && (
          <EditOrder
            isOpen={isEditMode}
            onClose={() => setEditMode(false)}
            onSaveChanges={(editedData) =>
              handleSaveChanges(editedData, selectedRow)
            }
            initialData={selectedRow as RowDataOrder}
          />
        )}
      </ScrollArea>
    </div>
  );
}

export default Order;
